// The Leads Loom pages' own copy of the Loom client adapter. It builds the same shapes the legacy
// client scenes read, so the read-only copies of those scenes under views/scenes/loom_lead stay
// recognisable, but it is independent of the adapter the legacy-backed Loom pages use. Loom kept
// the client status enum the legacy Client API used, plus the statuses lead intake adds.

import {buildFamilyMembers} from 'utils/backend/loom/family/family_adapter';

const personName = (person) => {
  return [person?.firstName, person?.lastName].filter(Boolean).join(' ');
};

const markdown = (blob) => ({blob: blob || '', editorType: 'MARKDOWN'});

// The legacy contacts array carried a free-text detail per channel and the scenes only print it, so
// Loom's two typed columns collapse back into that shape. Contacts.jsx branches on the literals
// Email and Phone.
const buildContacts = (person) => {
  const contacts = [];

  if (person?.email) contacts.push({type: 'Email', detail: person.email});
  if (person?.phoneNumber) contacts.push({type: 'Phone', detail: person.phoneNumber});

  return contacts;
};

// Every lead has a family member, and that member is the client's main contact by default. Intake
// on Loom main creates the family member but no client relationship (CQ-495 adds the link), so a
// client with no relationship lists its family members instead.
const buildFamilyContacts = (client) => {
  return (client?.family?.members || []).reduce((result, member, index) => ({
    ...result,
    [member.id]: {
      relationshipRn: member.id,
      name: personName(member.person),
      connectionType: member.personId === client.personId ? 'SELF' : null,
      priority: index + 1,
      description: markdown(client?.notes),
      contacts: buildContacts(member.person),
      roles: [],
    },
  }), {});
};

// Keyed by id like the legacy relationships, because the scenes call Object.values on them.
// connectionType is the Loom enum. Loom kept a single copy of the client's description as
// client.notes, which is what legacy wrote into each relationship's description.
const buildRelationships = (client) => {
  if (!client?.relationships?.length) {
    return buildFamilyContacts(client);
  }

  return client.relationships.reduce((result, relationship) => ({
    ...result,
    [relationship.id]: {
      relationshipRn: relationship.id,
      name: personName(relationship.familyMember?.person),
      connectionType: relationship.relationship,
      priority: relationship.priority,
      description: markdown(client?.notes),
      contacts: buildContacts(relationship.familyMember?.person),
      roles: (relationship.roles || []).map((role) => role.role).filter(Boolean),
    },
  }), {});
};

const buildCaregivers = (client) => {
  return (client?.caregiverAssignments || []).reduce((result, assignment) => ({
    ...result,
    [assignment.id]: {
      caregiverRn: assignment.caregiver?.legacyCaregiverRn,
      caregiverName: personName(assignment.caregiver?.person),
      status: assignment.status,
    },
  }), {});
};

// The migration parked legacy's one free-text address line whole in address.legacyAddress. The
// structured columns are read first, so this keeps working if Loom parses them later.
const buildAddress = (client) => {
  const [personAddress] = client?.person?.addresses || [];
  const address = personAddress?.address;

  if (!address) return '';

  return [address.line1, address.city, address.region].filter(Boolean).join(', ') ||
    address.legacyAddress ||
    '';
};

// Every section ships whether or not the client has a care_plan row, because the careplan scenes
// read .blob off their leaves. leadQuality sits on the client in Loom, and the lead card reads it
// from here.
const buildCarePlan = (client) => {
  const carePlan = client?.carePlan;

  return {
    leadQuality: client?.leadQuality,
    generalInformation: {
      clientName: personName(client?.person),
      address: buildAddress(client),
      clientInfo: markdown(carePlan?.clientInfo),
      notes: markdown(carePlan?.notes),
    },
    medicalHistory: {
      conditions: markdown(carePlan?.conditions),
      medications: markdown(carePlan?.medications),
    },
    careRoutine: {
      clientPreferences: markdown(carePlan?.clientPreferences),
      dailyTasks: markdown(carePlan?.dailyTasks),
    },
    caregiverRequirements: {
      careplanSummary: carePlan?.summary || '',
      scheduleRequirement: carePlan?.scheduleRequirement || '',
      matchDifficulty: carePlan?.matchDifficulty || '',
    },
    replacementPreference: client?.replacementPreference,
  };
};

// The card measures days in stage off this and falls back to createdAt when it is absent.
const buildLastStatusLog = (client) => {
  const [latest] = client?.statusHistory || [];

  if (!latest) return undefined;

  return {
    status: latest.status,
    lostReason: latest.lostReason,
    freeTextStatusReason: latest.freeTextStatusReason,
    createdAt: latest.createdAt,
  };
};

// clientId is Loom's own key. Intake leads created in Loom have no legacy RN, so these pages link
// and read by it.
const buildClientDetail = (client) => ({
  clientId: client.id,
  clientRn: client.legacyClientRn,
  clientFeClientRn: client.legacyClientRn || client.family?.clientFeClientRn || null,
  clientName: personName(client.person),
  status: client.status,
  relationships: buildRelationships(client),
  caregivers: buildCaregivers(client),
  clientCarePlan: buildCarePlan(client),
  clientLastLogStatusUpdate: buildLastStatusLog(client),
  replacementPreference: client.replacementPreference,
  description: client.notes ? {blob: client.notes, editorType: 'MARKDOWN'} : undefined,
  createdAt: client.createdAt,
  updatedAt: client.updatedAt,
});

const CAREVIEW_FOLLOW_UP_BY_LOOM_FOLLOW_UP = {
  ON_YES: {enabledOnYes: true, enabledOnNo: false},
  ON_NO: {enabledOnYes: false, enabledOnNo: true},
  BOTH: {enabledOnYes: true, enabledOnNo: true},
  NONE: {enabledOnYes: false, enabledOnNo: false},
};

// Multiple-choice options have no Loom column, so an answer that chose an option is the only
// surviving record of one. Answers pool across every questionnaire version, sorted because the
// author's ordering is not recoverable.
const buildOptionsByQuestion = (client) => {
  const chosen = {};

  for (const report of client?.shiftReports || []) {
    for (const answer of report.answers || []) {
      if (answer.answerTypeSnapshot !== 'MULTIPLE_CHOICE' || !answer.answerText) continue;

      if (!chosen[answer.questionId]) chosen[answer.questionId] = new Set();
      chosen[answer.questionId].add(answer.answerText);
    }
  }

  return chosen;
};

// The legacy Careview landed in shift_report. The active version is chosen rather than whichever
// row Postgres returned first.
const buildCareview = (client) => {
  const reports = client?.shiftReports || [];
  const report = reports.find((candidate) => candidate.status === 'ACTIVE') || reports[0];

  if (!report) return {};

  const optionsByQuestion = buildOptionsByQuestion(client);

  return {
    careviewRn: report.id,
    careviewQuestions: (report.questionItems || []).reduce((result, item) => ({
      ...result,
      [item.questionId]: {
        questionRn: item.questionId,
        type: item.question?.answerType,
        rankorder: item.sortOrder,
        questionData: {
          questionTitle: item.question?.prompt || '',
          isRequired: item.required,
          followUpYesNo:
            CAREVIEW_FOLLOW_UP_BY_LOOM_FOLLOW_UP[item.question?.followUp] ||
            CAREVIEW_FOLLOW_UP_BY_LOOM_FOLLOW_UP.NONE,
          ...(item.question?.answerType === 'MULTIPLE_CHOICE' && {
            options: [...(optionsByQuestion[item.questionId] || [])]
                .sort((a, b) => a.localeCompare(b))
                .map((optionTitle) => ({optionTitle, followUp: false})),
          }),
        },
      },
    }), {}),
  };
};

// Loom takes a status list and a row cursor. No limit is sent: the endpoint picks the page size.
export const toLoomLeadListQuery = (filters, sort, cursor) => {
  const query = {
    cursor,
    sortDirection: sort?.sortDirection?.toLowerCase() || 'desc',
  };
  const statuses = [];

  for (const filter of filters || []) {
    if (filter.key === 'status') {
      const values = Array.isArray(filter.value) ? filter.value : [filter.value];
      statuses.push(...values.filter((status) => status && status !== 'ALL'));
    }

    if (filter.key === 'searchString' && filter.value) query.search = filter.value;
  }

  if (statuses.length) query.status = [...new Set(statuses)].join(',');

  return query;
};

export const adaptLoomLeadList = (response) => ({
  list: (response?.clients || []).map(buildClientDetail),
  lastEvaluatedKey: response?.nextCursor || null,
});

export const adaptLoomLeadDetail = (client) => ({
  clientId: client.id,
  clientRn: client.legacyClientRn,
  clientDetail: {
    ...buildClientDetail(client),
    firstName: client.person.firstName,
    lastName: client.person.lastName,
    familyName: client.family.name,
    familyMembers: buildFamilyMembers(client),
    familyNotes: client.family.notes,
    clientNotes: client.notes,
  },
  careplan: buildCarePlan(client),
  careview: buildCareview(client),
});
